import { useMutation, useLazyQuery } from '@apollo/client/react';
import { useCallback, useMemo, useState } from 'react';

import { mapKickOffToState } from './KickOffMapper';
import { type ClientRepType, type KickOffContextProviderProps } from './types';

import { KickOffContext, type KickOffContextValueType } from '.';

import {
  type Activity,
  DEFAULT_SECTION,
  type ManagersType,
  type NoteSectionType,
  type Settings,
} from '@/components/KickOffs/types';
import {
  CreateKickOffDocument,
  GetKickOffDocument,
  type GetKickOffQuery,
  GetKickOffTemplateDocument,
  type GetKickOffTemplateQuery,
  type KickOffData,
  type KickOffTemplate,
  type Project,
  type ProjectKickOffs,
  UpdateKickOffActionsDocument,
  UpdateKickOffBasicsDocument,
  UpdateKickOffProjectDetailsDocument,
} from '@/graphql/generated/graphql';
import { type AddressType } from '@/types/globals';
import { DEFAULT_ADDRESS } from '@/utils/constants';
import { objectLength } from '@/utils/helperFunctions';
import { displayNotification } from '@/utils/notifications';

function filterValidAddresses(addresses: AddressType[]): AddressType[] {
  return addresses.filter((a) => a.street?.trim() && a.city?.trim() && a.zip?.trim());
}

const KickOffContextProvider = ({ children, projectId }: KickOffContextProviderProps) => {
  // ── State ──────────────────────────────────────────────────────────────────
  const [kickOffTitle, setKickOffTitle] = useState('');
  const [projectData, setProjectData] = useState<Partial<Project>>({});

  // Client — mutually exclusive: either selectedClientId (existing) or clientName (new)
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);
  const [clientCompanyNumber, setClientCompanyNumber] = useState<string | null>(null);
  const [clientReps, setClientReps] = useState<ClientRepType[]>([]);

  const [addresses, setAddresses] = useState<AddressType[]>([DEFAULT_ADDRESS]);
  const [noteSections, setNoteSections] = useState<NoteSectionType[]>([DEFAULT_SECTION]);
  const [managers, setManagers] = useState<ManagersType[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [contractTypeIds, setContractTypeIds] = useState<string[]>([]);
  const [projectTagIds, setProjectTagIds] = useState<string[]>([]);
  const [projectSettings, setProjectSettings] = useState<Settings[]>([]);
  const [sharedWithEmails, setSharedWithEmails] = useState<string[]>([]);
  const [generatedPDF, setGeneratedPDF] = useState<Blob | null>(null);
  const [selectedPreloadKickOff, setSelectedPreloadKickOff] = useState<Pick<ProjectKickOffs, 'id' | 'title'> | null>(
    null,
  );
  const [newTemplate, setNewTemplate] = useState<boolean>(false);
  const [newTemplateTitle, setNewTemplateTitle] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<Pick<KickOffTemplate, 'id' | 'title'> | null>(null);

  // ── Kick Off Lazy Query ────────────────────────────────────────────────────────────
  const [fetchFullKickOff, { data: activeKickOff, loading: fetchingFullDetails }] =
    useLazyQuery<GetKickOffQuery>(GetKickOffDocument);

  // ── TEmplate Lazy Query ────────────────────────────────────────────────────────────
  const [fetchTemplate, { loading: fetchingTemplateDetails }] =
    useLazyQuery<GetKickOffTemplateQuery>(GetKickOffTemplateDocument);

  // ── Hydrate state from draft ───────────────────────────────────────────────
  // Keyed on kickOffId so it re-runs only when a different draft is loaded.
  // Conditional checks ensure local edits are not overwritten by a stale refetch.

  const hydrateFromDraft = useCallback(
    ({ kickOff, template }: { kickOff?: ProjectKickOffs; template?: KickOffTemplate } = {}) => {
      const mapped = mapKickOffToState((kickOff ? kickOff?.data : template?.data) as KickOffData);

      if (kickOff) setProjectData(mapped.project);
      setSelectedClientId(mapped.client.id);
      setClientName(mapped.client.name);
      setClientCompanyNumber(mapped.client.companyNumber);
      setClientReps(mapped.client.reps);
      setAddresses(mapped.basics.addresses);
      setManagers(mapped.details.managers);
      setActivities(mapped.details.activities);
      setProjectSettings(mapped.details.settings);
      setContractTypeIds(mapped.details.contractTypeIds);
      setProjectTagIds(mapped.details.tagIds);
      setNoteSections(mapped.notes);
      setNewTemplate(mapped.template.newTemplate);
      setNewTemplateTitle(mapped.template.title);

      setKickOffTitle(
        kickOff?.title ?? `Kick-Off - ${mapped.project?.code ?? ''}-${projectData?.title ?? ''} - Rev.0.0.1`,
      );
    },
    [projectData.title],
  );

  // ── Preload KickOff Change ─────────────────────────────────────────────────────────
  const onPreloadKickOffChange = useCallback(
    (kickOff: Pick<ProjectKickOffs, 'id' | 'title'> | null) => {
      setSelectedPreloadKickOff(kickOff);

      if (kickOff) {
        fetchFullKickOff({ variables: { id: kickOff.id } }).then((res) => {
          hydrateFromDraft({ kickOff: res.data?.getKickOff as ProjectKickOffs });
        });
      }
    },
    [hydrateFromDraft, fetchFullKickOff],
  );

  // ── Preload Template Change ─────────────────────────────────────────────────────────
  const onPreloadTemplateChange = useCallback(
    (template: Pick<KickOffTemplate, 'id' | 'title'> | null) => {
      setSelectedTemplate(template);

      if (template) {
        fetchTemplate({ variables: { id: template.id } }).then((res) => {
          hydrateFromDraft({ template: res.data?.getKickOffTemplate as KickOffTemplate });
        });
      }
    },
    [fetchTemplate, hydrateFromDraft],
  );

  const basicsData = useMemo(
    () => ({
      // Always send clientId explicitly so switching from existing→new client nulls out the stale value
      clientId: selectedClientId ?? null,
      ...(clientName && { clientName }),
      ...(clientCompanyNumber && { clientCompanyNumber }),
      clientReps,

      // Project
      ...(projectData?.id && { projectId: projectData.id }),
      ...(projectData?.title && { projectTitle: projectData.title }),
      ...(projectData?.code && { projectCode: projectData.code }),

      addresses: filterValidAddresses(addresses),
      projectDetails: { managers: managers },
      newTemplate: newTemplate,
      newTemplateTitle: newTemplateTitle,
      ...(selectedTemplate && { templateId: selectedTemplate?.id }),
    }),
    [
      selectedClientId,
      clientName,
      clientCompanyNumber,
      clientReps,
      projectData,
      addresses,
      managers,
      newTemplate,
      newTemplateTitle,
    ],
  );

  // ── Mutations ──────────────────────────────────────────────────────────────
  const [createKickOff] = useMutation(CreateKickOffDocument, {
    onError: () => displayNotification({ type: 'error', text: 'Unable to create kick-off' }),
  });

  const [updateKickOffBasics] = useMutation(UpdateKickOffBasicsDocument, {
    refetchQueries: ['getKickOff'],
    onError: () => displayNotification({ type: 'error', text: 'Unable to save kick-off' }),
  });

  const [updateKickOffActions] = useMutation(UpdateKickOffActionsDocument, {
    refetchQueries: ['getKickOff'],
  });

  const [updateKickOffProjectDetails] = useMutation(UpdateKickOffProjectDetailsDocument, {
    refetchQueries: ['getKickOff'],
  });

  // ── updateNotes ────────────────────────────────────────────────────────────
  const updateNotes = useCallback(
    (sections: NoteSectionType[]) => {
      if (!activeKickOff?.getKickOff.id) return;
      updateKickOffActions({
        variables: { kickOffId: activeKickOff.getKickOff.id, kickOffActions: sections },
      });
    },
    // eslint-disable-next-line
    [],
  );

  // ── SaveAndNext ────────────────────────────────────────────────────────────
  const SaveAndNext = useCallback(
    (currentStep: number) => {
      if (currentStep === 0) {
        if (activeKickOff?.getKickOff.id) {
          updateKickOffBasics({
            variables: { id: activeKickOff.getKickOff.id, title: kickOffTitle, data: basicsData },
          });
        } else {
          createKickOff({
            variables: {
              title: kickOffTitle,
              data: {
                ...basicsData,
                projectDetails: {
                  managers: managers,
                  activities: activities,
                  contractTypeIds: contractTypeIds,
                  projectTagIds: projectTagIds,
                  projectSettings: projectSettings,
                },
                ...(objectLength(noteSections) > 0 && { notes: noteSections }),
              },
            },
            onCompleted: (res) => {
              fetchFullKickOff({ variables: { id: res.createKickOff } }).then((res) => {
                hydrateFromDraft({ kickOff: res.data?.getKickOff as ProjectKickOffs });
              });
            },
          });
        }
      }

      if (!activeKickOff?.getKickOff.id) return;

      if (currentStep === 1) {
        updateKickOffActions({
          variables: {
            kickOffId: activeKickOff.getKickOff.id,
            kickOffActions: noteSections,
          },
        });
      }

      if (currentStep === 2) {
        updateKickOffProjectDetails({
          variables: {
            kickOffId: activeKickOff.getKickOff.id,
            managers,
            activities,
            contractTypeIds,
            projectTagIds,
            projectSettings,
          },
        });
      }
    },
    [
      activeKickOff?.getKickOff.id,
      kickOffTitle,
      basicsData,
      noteSections,
      managers,
      activities,
      contractTypeIds,
      projectTagIds,
      projectSettings,
      createKickOff,
      fetchFullKickOff,
      hydrateFromDraft,
      updateKickOffBasics,
      updateKickOffActions,
      updateKickOffProjectDetails,
    ],
  );

  // ── Context value ──────────────────────────────────────────────────────────
  const context: KickOffContextValueType = {
    kickOffId: activeKickOff?.getKickOff.id || null,
    kickoffData: activeKickOff?.getKickOff,
    loadingContext: fetchingFullDetails || fetchingTemplateDetails,
    projectData,
    setProjectData,
    selectedClientId,
    setSelectedClientId,
    clientName,
    setClientName,
    clientCompanyNumber,
    setClientCompanyNumber,
    clientReps,
    setClientReps,
    kickOffTitle,
    setKickOffTitle,
    addresses,
    setAddresses,
    noteSections,
    setNoteSections,
    managers,
    setManagers,
    activities,
    setActivities,
    contractTypeIds,
    setContractTypeIds,
    projectTagIds,
    setProjectTagIds,
    projectSettings,
    setProjectSettings,
    sharedWithEmails,
    setSharedWithEmails,
    generatedPDF,
    setGeneratedPDF,
    updateNotes,
    SaveAndNext,
    selectedPreloadKickOff,
    onPreloadKickOffChange,
    newTemplate,
    setNewTemplate,
    newTemplateTitle,
    setNewTemplateTitle,
    selectedTemplate,
    onPreloadTemplateChange,
    existingProjectId: projectId,
  };

  return <KickOffContext.Provider value={context}>{children}</KickOffContext.Provider>;
};

export default KickOffContextProvider;
